import { Action } from '@reduxjs/toolkit';
import {
  equals, includes, isNil, map as rMap,
} from 'ramda';
import { combineEpics, StateObservable } from 'redux-observable';
import {
  concat, EMPTY, interval, of, Subject, timer, Subscription, Observable, from, empty,
} from 'rxjs';
import {
  catchError, delayWhen, first, flatMap, map, pluck, switchMap,
  takeUntil, distinctUntilChanged, filter, ignoreElements, delay,
  mergeMap,
} from 'rxjs/operators';
import { webSocket } from 'rxjs/webSocket';

import { logError } from '@ezugi/bootstrap/src/util/logUtils';
import {
  DISCONNECTED, NO_MORE_BETS, INITIAL_DATA, PLACE_BET_INTENT_POOL, ERRORS, RECONNECTING,
  BonusMessage,
} from '@ezugi/constants';

import { state$ as stateStream } from '../../../streams';
import { wallclockRef } from '../../../views/GameVideo/EvoPlayer/hooks/usePlayerEvents';
import {
  dialogActions,
  errorActions,
  roundActions,
  socketActions,
  tipsActions,
  videoActions,
} from '../../actions';
import { RECONNECTING_SCREEN_DIALOG, RECONNECTING_SOCKET_DIALOG } from '../../constants/dialog';
import { dialogNameSelector, isEvoPlayerSelector, useDynamicVideoDelaySelector } from '../../selectors';
import { promptStatesSelector } from '../../selectors/app';
import { socketReconnectSelector } from '../../selectors/socket';
import { videoCDNListSelector, videoDelaySelector, videoIsPlayingSelector } from '../../selectors/video';
import { RootState } from '../../types';
import handleErrors from './handleErrors';
import handleRoundStatus, { TIP_OUT_OF_LIMITS, TIP_PLACED } from './handleRoundStatus';
import { pluckSocketParams } from './utils';

const { GAME_LOAD_FAILURE, CODES } = ERRORS;

const { socket } = socketActions;
const { round } = roundActions;
const { tips } = tipsActions;
const { dialog } = dialogActions;
const { video } = videoActions;

export const openEvents = new Subject();
const closeEvents = new Subject();
const errorEvents = new Subject();

const SOCKET_PING_INTERVAL = 30000;
const MAX_FAST_RECONNECT_THRESHOLD = 10;

/**
 * Used to prevent unneeded store updates
 * Current usecase: PlaceBetIntentPool is received from the server every second
 * even if the data is exactly the same
 *
 * Using `distinctUntilChanged` without the compareFn would not work
 * because the server also sends a timestamp `RoundTripStartTime`
 * @param {*} previous
 * @param {*} current
 * @returns true if there are no changes
 */
function socketMessageComparer(previous: any, current: any) {
  // We check for `PoolData` and `MessageType` only on current message
  // If previous message does not have `PoolData` and/or has a different `MessageType`
  // The update should go through
  if (current?.MessageType === PLACE_BET_INTENT_POOL && !isNil(current?.PoolData)) {
    return equals(previous?.PoolData, current.PoolData);
  }

  return false;
}

const updateStore = ({
  // newline
  socketMessage,
  state,
  handlersMap = {},
}: {
  socketMessage: any;
  state: RootState;
  handlersMap: any;
}) => handleErrors(socketMessage, state, handlersMap) || handleRoundStatus(socketMessage, state) || EMPTY;

const socketEpic = (handlersMap: any) => (
  action$: Observable<any>,
  state$: StateObservable<RootState>
): Observable<any> => action$.pipe(
  filter(socket.request.match),
  map(({ payload }) => payload),
  switchMap((extraParameters = {}) => {
    const { url, params } = pluckSocketParams(state$.value, extraParameters);
    const ws = webSocket({
      url,
      openObserver: openEvents,
      closeObserver: closeEvents,
    });

    if (import.meta.env.MODE === 'development') {
      // @ts-ignore
      window.__SOCKET__ = ws;
    }

    ws.subscribe({
      error: (err) => errorEvents.next(err),
    });

    let errorSub: Subscription | null = null;
    let closeSub: Subscription | null = null;

    openEvents.subscribe(() => ws.next(params));

    closeSub = closeEvents.subscribe(() => {
      closeSub && closeSub.unsubscribe();
      errorSub && errorSub.unsubscribe();
    });

    errorSub = errorEvents.subscribe(() => {
      closeSub && closeSub.unsubscribe();
      errorSub && errorSub.unsubscribe();
    });

    action$
      .pipe(
        filter((action) => socket.send.type === action.type || socket.success.type === action.type),
        switchMap(() => interval(SOCKET_PING_INTERVAL).pipe(
          takeUntil(action$.pipe(filter((action) => action.type === socket.request.type
          || socket.failure.type === action.type
          || socket.close.type === action.type)))
        ))
      )
      .subscribe({
        next: () => ws.next && ws.next('EzugiPingMessage'),
      });

    action$
      .pipe(
        filter(tips.send.match),
        pluck('payload'),
        takeUntil(action$.pipe(filter((action) => action.type === socket.request.type
        || socket.failure.type === action.type
        || socket.close.type === action.type)))
      )
      .subscribe({
        next: (data) => ws.next(data),
      });

    action$
      .pipe(
        filter(socket.send.match),
        pluck('payload')
      )
      .subscribe({
        next: (data) => ws.next(data),
      });

    action$
      .pipe(
        filter(socket.close.match),
        first(),
        takeUntil(action$.pipe(filter((action) => action.type === socket.request.type
        || socket.failure.type === action.type)))
      )
      .subscribe({
        next: () => ws.complete(),
      });

    const isEvoPlayer = isEvoPlayerSelector(state$.value);
    const useDynamicVideoDelay = useDynamicVideoDelaySelector(state$.value);

    let previousEvoDelay = videoDelaySelector(state$.value);
    let previousWallclock: number | null = null;

    return ws.pipe(
      delayWhen(({ MessageType, RoundTripStartTime = 0 }: any) => {
        const videoCDNList = videoCDNListSelector(state$.value);
        const wallclock = wallclockRef.current || 0;
        const nanoDelay = videoDelaySelector(state$.value);
        const isVideoPlaying = videoIsPlayingSelector(state$.value);
        const promptStates = promptStatesSelector(state$.value);

        const evoDelay = (isVideoPlaying && wallclock > 0 && wallclock !== previousWallclock)
          ? Math.max(RoundTripStartTime - wallclock, 0) / 1000 : previousEvoDelay;

        previousEvoDelay = evoDelay;
        previousWallclock = wallclock;

        const videoDelay = isEvoPlayer && useDynamicVideoDelay ? evoDelay : nanoDelay;

        if (includes(MessageType, [
          TIP_PLACED,
          TIP_OUT_OF_LIMITS,
          // INITIAL_DATA should not be delayed
          INITIAL_DATA,
          // NO_MORE_BETS state is handled as soon as the client receives it
          NO_MORE_BETS,
          // Bonus should not be delayed
          BonusMessage.BonusWheelStart,
          BonusMessage.BonusWheelFinished,
          ...(promptStates || []),
        ])) {
          return of(undefined);
        }

        (stateStream as any).dispatch(video.set({ cdnList: { ...videoCDNList, videoDelay } }));

        return timer(videoDelay * 1000);
      }),
      distinctUntilChanged(socketMessageComparer),
      flatMap((socketMessage) => concat(
        // newline
        updateStore({ socketMessage, state: state$.value, handlersMap }),
        of(socket.message(socketMessage))
      )),
      takeUntil(action$.pipe(filter((action) => socket.request.type === action.type
       || socket.failure.type === action.type
       || socket.close.type === action.type))),
      catchError((error) => {
        logError(error, 'error');
        return of(socket.failure({ error:
          { message: error.message || `WebSocket connection to ${error?.target?.url} failed.` } } as any));
      })
    );
  }),
  catchError(() => of(
    errorActions.error.set({
      code: CODES[GAME_LOAD_FAILURE],
      message: 'Game load failure',
      title: 'errors.game_load_failure_title',
      description: 'errors.game_load_failure_desc',
      actionLabel: 'general.go_back',
    })
  ))
);

const socketErrorEpic = (action$: Observable<any>) => action$.pipe(
  filter(socket.success.match),
  switchMap(() => errorEvents.asObservable()),
  map(() => round.set({ roundStatus: DISCONNECTED }))
);

const socketReconnectedEpic = (action$: Observable<Action>) => action$.pipe(
  filter((action: any) => action.type === socket.failure.type),
  switchMap(() => action$.pipe(
    filter((action: any) => action.type === socket.success.type),
    first()
  )),
  map(() => [ dialog.remove(), video.reconnectAfterNanoMediaFailed() ]),
  mergeMap((result) => {
    const actions = rMap(of, result);
    return concat(...actions);
  })
);

const hasNetworkConnection = async () => {
  const healthcheckUrl = import.meta.env.DEV ? '/healthcheck.json' : import.meta.env.VITE_HEALTHCHECK_URL;

  const hasInternet = await fetch(healthcheckUrl, {
    method: 'HEAD',
    cache: 'no-cache',
  });

  if (!hasInternet?.ok) {
    throw Error('No internet connection');
  }
};

const networkCheckEpic = (action$: Observable<Action>) => action$.pipe(
  filter(socket.success.match),
  switchMap(() => timer(0, 5000).pipe(
    switchMap(() => from(hasNetworkConnection())),
    ignoreElements(),
    catchError(() => of(
      socket.failure(),
    ))
  ))
);

const socketReconnectEpic = (action$: Observable<Action>) => action$.pipe(
  filter(socket.failure.match),
  switchMap(() => timer(5000).pipe(
    takeUntil(
      action$.pipe(
        filter(socket.request.match),
        first()
      )
    ),
    map((data: any) => data)
  )),
  map(socket.reconnect)
);

const socketReconnectRequestEpic = (action$: Observable<Action>, state$: StateObservable<RootState>) => action$.pipe(
  filter(socket.reconnect.match),
  switchMap(() => {
    const count = socketReconnectSelector(state$.value) as number;
    const dialogName = dialogNameSelector(state$.value);

    return concat(
      count > MAX_FAST_RECONNECT_THRESHOLD
        ? dialogName === RECONNECTING_SOCKET_DIALOG ? empty() : of(dialog.add({ name: RECONNECTING_SOCKET_DIALOG }))
        : dialogName === RECONNECTING_SCREEN_DIALOG ? empty() : of(dialog.add({
          name: RECONNECTING_SCREEN_DIALOG,
        })),
      of(round.set({ roundStatus: RECONNECTING })),
      of(socket.request()).pipe(delay(count > MAX_FAST_RECONNECT_THRESHOLD ? 5000 : 1000))
    );
  })
);

/**
 * The current socket connection is closed and a new one is started
 *  with socket.request and an updated URL from state
 */
const moveTableEpic = (action$: Observable<any>) => action$.pipe(
  filter(socket.moveTable.match),
  flatMap(() => concat(
    of(socket.close()),
    of(socket.request({ fromReconnectToServer: true })),
  ))
);

export default (handlersMap: any) => combineEpics(
  socketEpic(handlersMap),
  socketErrorEpic,
  networkCheckEpic,
  socketReconnectEpic,
  socketReconnectRequestEpic,
  socketReconnectedEpic,
  moveTableEpic
);
